import { GenericTextOperators } from '@mairistem/tools';
import dayjs from 'dayjs';
import _ from 'lodash';

import type { RecuEncaissementType, RegieRegisseurType } from '@/types';
import type { RecuListeItemType } from '@/types/recu';
import { getRegieRegisseurFullName } from './regisseur';
import * as utils_string from './string';

export type FormatRecuToListeItemType = (recu: RecuEncaissementType) => RecuListeItemType;

export const formatRecuToListeItem: FormatRecuToListeItemType = (recu) => {
  const {
    identifiant,
    numero,
    dateCreation,
    regisseurAnnulation,
    regisseur,
    factures,
    lignes,
    adresse,
  } = recu;

  const { payeur } = _.head(factures) || {};
  const { nom, prenom, civilite } = payeur || {};

  const { abreviation: abreviationCivilite } = civilite || {};

  const montantRegle = _.sumBy(lignes, 'montant');
  const isAnnule = !_.isNil(regisseurAnnulation);
  const identite = `${abreviationCivilite || ''} ${utils_string.toOfficialLastname(nom || '')} ${utils_string.toOfficialFirstname(prenom || '')}`;
  // Un reçu sans régisseur est un paiement en ligne, généré automatiquement depuis MFO.
  const identiteRegisseurCreation = _.isNil(regisseur)
    ? 'Paiement en ligne'
    : `${regisseur.prenom || ''} ${regisseur.nom || ''}`;
  const identiteRegisseur = isAnnule
    ? `${regisseurAnnulation?.prenom || ''} ${regisseurAnnulation?.nom || ''}`
    : identiteRegisseurCreation;
  const numeroFactures = _.join(
    _.map(factures, ({ numero: numeroFacture, cycle }) => {
      // Au comptant, la facture est rattachée au cycle technique de la régie : sa date
      // d'émission n'a rien à présenter au régisseur, on ne garde que le numéro.
      if (cycle?.comptant || _.isNil(cycle?.dateEmission)) {
        return `${numeroFacture}`;
      }

      return `${numeroFacture} du ${dayjs(cycle.dateEmission).format('DD/MM/YYYY')}`;
    }),
    ' / ',
  );

  let adrPayeur = '';

  if (_.isNumber(adresse?.numeroVoirie) && adresse?.numeroVoirie !== 0) {
    adrPayeur += `${adresse?.numeroVoirie} `;
  }

  if (!_.isEmpty(adresse?.lettre)) {
    adrPayeur += `${adresse?.lettre} `;
  }

  if (!_.isEmpty(adresse?.complementNumeroVoirie)) {
    adrPayeur += `${adresse?.complementNumeroVoirie} `;
  }

  if (!_.isEmpty(adresse?.libelleVoie)) {
    adrPayeur += `${adresse?.libelleVoie}`;
  }

  if (!_.isEmpty(adresse?.numeroCodePostal)) {
    adrPayeur += ` ${adresse?.numeroCodePostal}`;
  }

  if (!_.isEmpty(adresse?.localite)) {
    adrPayeur += ` ${adresse?.localite}`;
  }

  if (!_.isEmpty(adresse?.libellePays)) {
    adrPayeur += ` ${adresse?.libellePays}`;
  }

  return {
    identifiant,
    numero,
    nomPayeur: _.trim(identite),
    dateCreation,
    adressePayeur: adrPayeur,
    numeroFacturesEncaissees: numeroFactures,
    montant: montantRegle,
    annule: isAnnule,
    regisseur: _.trim(identiteRegisseur),
  };
};

export const formatRecusToListeItem = (recus) => _.map(recus, formatRecuToListeItem);

export const getRecusListeDefaultFilters = (connectedRegisseur: RegieRegisseurType) => [
  {
    // Utilisé par le useListeSearchSort
    operator: 'DANS_LISTE',
    textOperator: GenericTextOperators.DANS_LISTE,
    propertyName: 'regisseur',
    value: [getRegieRegisseurFullName(connectedRegisseur)],
    // Ci-dessous, normalement pas utilisé mais obligatoire pour TS
    type: 'string',
    predicate: null,
  },
];
